import { useRef, useCallback, useEffect } from 'react';
import axios, { AxiosInstance, CancelTokenSource } from 'axios';

import { smt, RequestConfig, RequestError } from '.';
import { call } from 'utils';

type Config = {
  api?: AxiosInstance;
  autoclear?: boolean;
};

const useRequest = (config?: Config) => {
  const sources = useRef<CancelTokenSource[]>([]);
  const { api, autoclear } = config || {};

  const request = useCallback(
    <T>(config: RequestConfig<T>) => {
      const instance = api || smt;
      const source = axios.CancelToken.source();
      const { onSuccess, onError, onCancel, onComplete, ...rest } = config;

      if (autoclear) {
        for (const source of sources.current) {
          source.cancel('autoclear');
        }

        sources.current = [];
      }

      sources.current.push(source);

      instance
        .request<T>({ ...rest, cancelToken: source.token })
        .then(({ data }) => {
          try {
            call(onSuccess, data);
          } catch (_err) {}
        })
        .catch((err: RequestError<T>) => {
          if (axios.isCancel(err)) {
            call(onCancel, err.message !== 'unmount');
          } else {
            call(onError, err);
          }
        })
        .finally(() => {
          sources.current.splice(sources.current.indexOf(source), 1);
          call(onComplete);
        });

      return source;
    },
    [api, autoclear]
  );

  useEffect(
    () => () => {
      for (const source of sources.current) {
        source.cancel('unmount');
      }
    },
    []
  );

  return request;
};

export default useRequest;
